import { Plus } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { useT } from '@/i18n'
import { warehousesService } from '@/services'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { NumberInput } from '@/components/ui/MaskedInput'
import { controlClass } from '@/components/ui/controlClass'
import { cn } from '@/lib/cn'
import { formatNumber, pickName, trimDecimal } from '@/lib/format'
import type { ContentLocale, Package, Product, WarehouseStockOption } from '@/types/api'

/** Signed decimal text with up to 4 places, as the backend stores it. */
const SCALE = 4
const DECIMAL = /^-?\d+(\.\d+)?$/

function toUnits(value?: string): bigint | null {
  const text = (value ?? '').trim()
  if (text === '') return 0n
  if (!DECIMAL.test(text)) return null
  const negative = text.startsWith('-')
  const [int, frac = ''] = text.replace('-', '').split('.')
  if (frac.length > SCALE) return null
  const units = BigInt(int + frac.padEnd(SCALE, '0'))
  return negative ? -units : units
}

function fromUnits(units: bigint): string {
  const negative = units < 0n
  const digits = (negative ? -units : units).toString().padStart(SCALE + 1, '0')
  const text = `${digits.slice(0, -SCALE)}.${digits.slice(-SCALE)}`.replace(/\.?0+$/, '')
  return negative && text !== '0' ? `-${text}` : text
}

export function addAmounts(...values: (string | undefined)[]): string {
  let total = 0n
  for (const value of values) total += toUnits(value) ?? 0n
  return fromUnits(total)
}

export function subtractAmounts(left?: string, right?: string): string {
  return fromUnits((toUnits(left) ?? 0n) - (toUnits(right) ?? 0n))
}

export function roundTo2(value: string): string {
  const units = toUnits(value) ?? 0n
  const step = 100n
  const rounded = ((units >= 0n ? units + step / 2n : units - step / 2n) / step) * step
  return fromUnits(rounded)
}

export function isAmount(value?: string, allowEmpty = true): boolean {
  const text = (value ?? '').trim()
  if (text === '') return allowEmpty
  return /^\d+(\.\d{1,4})?$/.test(text)
}

export function isPositive(value?: string): boolean {
  return isAmount(value, false) && (toUnits(value) ?? 0n) > 0n
}

/**
 * Whether a line's kg comes from its package (count × package weight), which
 * locks the kg input: not for Belarus or uncut meat, nor for a package with no
 * weight, whose kg is typed.
 */
export function weighsByPackage(line: { is_belarus: boolean; is_uncut: boolean; packaging_id: string }, packages: Package[]) {
  if (line.is_belarus || line.is_uncut || !line.packaging_id) return false
  const pack = packages.find((item) => item.id === line.packaging_id)
  return Boolean(pack && Number(pack.weight) > 0)
}

/** Package count × package weight in kg (grams for a gram package). */
export function packageKilograms(count: string, pack?: Package): string {
  if (!pack || !(Number(pack.weight) > 0) || !isAmount(count, false)) return ''
  const units = (toUnits(count) ?? 0n) * (toUnits(pack.weight) ?? 0n)
  const kg = pack.unit_code === 'g' ? units / 1000n : units
  return fromUnits(kg / 10n ** BigInt(SCALE))
}

export function toLocalDateTime(value?: string) {
  const date = value ? new Date(value) : new Date()
  const offset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

/**
 * A line's «Остаток» box: what the chosen warehouse (and cell) holds of its
 * product now, as the product list counts it (like МойСклад's «Остаток»):
 * Belarus meat as «П 40 · З 90 · 900 кг», other products as «900 кг». One
 * read-only box like the totals; empty without a product.
 */
export function StockCell({ productId, stock, units }: {
  productId: string
  stock: WarehouseStockOption[]
  units: { kg: string; front: string; rear: string }
}) {
  let text = ''
  let belarus = false
  if (productId) {
    const option = stock.find((item) => item.product_id === productId)
    const weight = `${formatNumber(option?.weight ?? '0')} ${units.kg}`
    belarus = Boolean(option?.is_belarus_meat)
    text = option?.is_belarus_meat
      ? `${units.front} ${formatNumber(option.front_count)} · ${units.rear} ${formatNumber(option.rear_count)} · ${weight}`
      : weight
  }
  return <Input className={cn('h-9 font-medium whitespace-nowrap', belarus ? 'min-w-48' : 'min-w-24')} value={text} readOnly tabIndex={-1} title={text} />
}

export function productOptionLabel(product: { product_name_en: string; product_name_ru: string; product_name_uz: string; product_name_cyr: string }, locale: ContentLocale) {
  return pickName(
    { name_en: product.product_name_en, name_ru: product.product_name_ru, name_uz: product.product_name_uz, name_cyr: product.product_name_cyr },
    locale,
  )
}

export function packageOptions(product: Product | undefined, packages: Package[], locale: ContentLocale) {
  if (!product) return []
  const assigned = new Set(product.package_ids)
  return packages.filter((item) => assigned.has(item.id)).map((item) => ({ value: item.id, label: pickName(item, locale) }))
}

/** A read-only header value, as Ucode greys out derived fields. */
export function ReadonlyField({ label, value, numeric }: { label: string; value?: string; numeric?: boolean }) {
  return (
    <Field label={label}>
      <Input
        className={cn('h-9', numeric && 'num')}
        value={numeric ? (value ? formatNumber(value) : '') : (value ?? '')}
        placeholder="—"
        readOnly
        tabIndex={-1}
      />
    </Field>
  )
}

export function Block({ title, action, children, className }: { title: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    // Its table is exported from the document's title bar (DocumentTableExport), not from here.
    <div data-export-block data-export-title={title} className={cn('min-w-0 rounded-[calc(10px*var(--radius-scale))] border border-line p-3', className)}>
      <div className="mb-2 flex min-h-7 items-center justify-between gap-2">
        <h3 className="text-[length:calc(13px*var(--text-scale))] font-semibold">{title}</h3>
        {action}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  )
}

/**
 * Line tables span the block; an input's `width` is its minimum, so the
 * fields share the spare width and grow together (№ and the remove button
 * stay narrow with `w-px`).
 */
export const headCell = 'h-7 pr-2 text-left text-[length:calc(12px*var(--text-scale))] font-medium text-muted whitespace-nowrap'
export const bodyCell = 'py-1 pr-2 align-middle'

/**
 * An amount cell. Without `onChange` it only shows a value (white, read-only);
 * a `disabled` one is locked by a rule of the line (gray, not focusable) and
 * says why on hover with `lockedReason`.
 */
export function AmountInput({
  value,
  onChange,
  disabled,
  lockedReason,
  invalid,
  width = 'min-w-20',
  label,
}: {
  value: string
  onChange?: (value: string) => void
  disabled?: boolean
  lockedReason?: string
  invalid?: boolean
  width?: string
  label: string
}) {
  const readOnly = !onChange
  return (
    <NumberInput
      aria-label={label}
      title={disabled ? lockedReason : undefined}
      className={cn('h-9', width, invalid && !disabled && 'border-danger')}
      decimals={SCALE}
      value={value}
      readOnly={readOnly}
      disabled={disabled && !readOnly}
      tabIndex={readOnly ? -1 : undefined}
      onValueChange={onChange}
    />
  )
}

export function TotalCell({ value, width = 'min-w-20' }: { value: string; width?: string }) {
  return <NumberInput className={cn('h-9 font-medium', width)} value={value} readOnly tabIndex={-1} />
}

/**
 * The «Добавить» action of a line block. While lines cannot be added yet (no
 * warehouse chosen) the button stays visible but disabled, with the reason
 * beside it (`disabled` alone gives no reason). Its hint lists the keys of the
 * keyboard entry (workbench/keyboardEntry.ts, which clicks this button on «+»):
 * the block's header keeps only «Добавить» (client 2026-10-25, a «?» of its own
 * there read as a stray icon).
 */
export function AddLineAction({ blocked, disabled, onAdd }: { blocked?: string; disabled?: boolean; onAdd: () => void }) {
  const t = useT()
  const keys = [
    t('warehouse.documents.keys.title'),
    t('warehouse.documents.keys.add'),
    t('warehouse.documents.keys.next'),
    t('warehouse.documents.keys.back'),
    t('warehouse.documents.keys.pick'),
    t('warehouse.documents.keys.save', { key: isMac ? '⌘S' : 'Ctrl+S' }),
    t('warehouse.documents.keys.remove', { key: isMac ? 'Ctrl+Delete / ⌘⌫' : 'Ctrl+Delete' }),
  ]
  return (
    <div className="flex min-w-0 items-center gap-2">
      {blocked ? (
        <span className="truncate text-[length:calc(12px*var(--text-scale))] text-muted">{blocked}</span>
      ) : null}
      <Button
        data-line-add
        variant="ghost"
        size="sm"
        icon={<Plus className="size-3.5" />}
        disabled={Boolean(blocked) || disabled}
        title={keys.join('\n')}
        onClick={onAdd}
      >
        {t('common.actions.add')}
      </Button>
    </div>
  )
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

/** Stands in for the lines and their totals while a block has none, saying what to do next. */
export function EmptyLinesRow({ colSpan, text }: { colSpan: number; text: string }) {
  return (
    <tr>
      <td colSpan={colSpan} className="pt-1">
        <div className="rounded-[calc(8px*var(--radius-scale))] border border-dashed border-line-strong px-3 py-4 text-center text-[length:calc(13px*var(--text-scale))] text-muted">
          {text}
        </div>
      </td>
    </tr>
  )
}

/**
 * Adds the first empty line as soon as lines can be added (a warehouse is
 * chosen) and there are none, so the inputs appear without a click. Only the
 * change to «can add» triggers it: removing the last line does not re-add one.
 */
export function useFirstLine(ready: boolean, empty: boolean, add: () => void) {
  const wasReady = useRef(ready)
  const addLine = useRef(add)
  useEffect(() => {
    addLine.current = add
  })
  useEffect(() => {
    if (ready && !wasReady.current && empty) addLine.current()
    wasReady.current = ready
  }, [ready, empty])
}

export const plain = (value?: string) => trimDecimal(value ?? '') || '0'

/**
 * The people who can receive or count in a warehouse: its employees, as the
 * legacy forms list them. A saved receiver who left keeps showing by name.
 */
export function WarehouseEmployeeSelect({
  warehouseId,
  value,
  onChange,
  label,
  savedName,
  disabled,
  error,
  personnel = false,
}: {
  warehouseId: string
  value: string
  onChange: (value: string) => void
  label: string
  savedName?: string
  disabled?: boolean
  error?: string
  personnel?: boolean
}) {
  const t = useT()
  // The loaded list belongs to one warehouse; another warehouse shows none until it loads.
  const [loaded, setLoaded] = useState<{ warehouseId: string; items: { value: string; label: string }[] }>({ warehouseId: '', items: [] })
  const previousWarehouse = useRef(warehouseId)
  useEffect(() => {
    if (previousWarehouse.current !== warehouseId) {
      previousWarehouse.current = warehouseId
      onChange('')
    }
  }, [warehouseId, onChange])
  useEffect(() => {
    if (!warehouseId) return
    const controller = new AbortController()
    const request = personnel
      ? warehousesService.listHREmployees(warehouseId, { signal: controller.signal }).then((items) => items.filter((item) => item.status === 'working').map((item) => ({ value: item.employee_id, label: item.display_name })))
      : warehousesService.listEmployees(warehouseId, { signal: controller.signal }).then((items) => items.map((item) => ({ value: item.profile_id, label: item.display_name })))
    void request.then((items) => setLoaded({ warehouseId, items }))
      .catch(() => undefined)
    return () => controller.abort()
  }, [warehouseId, personnel])
  const employees = loaded.warehouseId === warehouseId ? loaded.items : []
  const options = value && savedName && !employees.some((item) => item.value === value) ? [{ value, label: savedName }, ...employees] : employees
  return (
    <Field label={label} required error={error}>
      <Select
        value={value}
        placeholder={warehouseId ? t('warehouse.documents.selectEmployee') : t('warehouse.documents.selectWarehouseFirst')}
        options={options}
        disabled={disabled || !warehouseId}
        onValueChange={onChange}
      />
    </Field>
  )
}

/**
 * A line's product once it cannot be picked (a saved line, or one that comes
 * from a truck or a shipment): the same box and width as the picker it is
 * chosen with on create, read-only; a long name wraps inside it instead of
 * being cut.
 */
export function ProductNameBox({ name, className }: { name: string; className?: string }) {
  return (
    <div
      aria-readonly
      title={name}
      className={cn(
        controlClass,
        'flex min-h-[calc(var(--spacing)*8*var(--ui-size))] w-80 items-center whitespace-normal break-words bg-field-disabled py-1 font-medium shadow-none',
        'text-[length:calc(13px*var(--text-scale)*var(--ui-size))] hover:border-field-border',
        className,
      )}
    >
      {name || '—'}
    </div>
  )
}
